/**
 * Travel Destinations Database
 * Source: TravelHealthPro (https://travelhealthpro.org.uk/)
 * Last updated: January 2025
 */

interface VaccineRecommendation {
  name: string;
  type: "required" | "recommended" | "optional";
  description: string;
  schedule?: string;
  notes?: string;
  id?: string; // Optional ID for reference
}

export interface TravelDestination {
  id: string;
  name: string;
  region: string;
  riskLevel: "low" | "medium" | "high";
  vaccines: VaccineRecommendation[];
  generalAdvice: string;
  malariaRisk?: string;
  yellowFeverRequired?: boolean;
}

// Common vaccine recommendations by region
const commonVaccines = {
  hepatitisA: {
    name: "Hepatitis A",
    type: "recommended" as const,
    description: "Recommended for most travelers",
    schedule: "Single dose, booster after 6-12 months",
    notes: "Protection against contaminated food and water",
  },
  typhoid: {
    name: "Typhoid",
    type: "recommended" as const,
    description:
      "Recommended for most travelers, especially those visiting smaller cities",
    schedule: "Single injection or oral capsules",
    notes: "Protection against typhoid fever from contaminated food/water",
  },
  hepatitisB: {
    name: "Hepatitis B",
    type: "optional" as const,
    description: "For longer stays or potential medical care",
    schedule: "3 doses over 6 months (accelerated schedule available)",
    notes: "Consider for extended stays or potential medical procedures",
  },
  rabies: {
    name: "Rabies",
    type: "optional" as const,
    description: "For travelers with extensive outdoor/rural activities",
    schedule: "3 doses over 21-28 days",
    notes:
      "Consider for adventure travelers, long-term travelers, or those working with animals",
  },
  japaneseEncephalitis: {
    name: "Japanese Encephalitis",
    type: "optional" as const,
    description: "For travelers to rural areas, especially during rainy season",
    schedule: "2 doses over 28 days",
    notes: "Recommended for stays >1 month in rural areas",
  },
  yellowFever: {
    name: "Yellow Fever",
    type: "required" as const,
    description: "Required for entry or recommended for certain regions",
    schedule: "Single dose (lifetime protection)",
    notes: "Certificate required for entry. Valid 10 days after vaccination.",
  },
  meningococcal: {
    name: "Meningococcal Meningitis",
    type: "optional" as const,
    description: "For travelers to high-risk areas",
    schedule: "Single dose",
    notes: "Higher risk during dry season in some regions",
  },
  cholera: {
    name: "Cholera",
    type: "optional" as const,
    description: "Only for specific high-risk situations",
    schedule: "2 oral doses",
    notes: "Usually only for aid workers or those in high-risk areas",
  },
  dengue: {
    name: "Dengue",
    type: "optional" as const,
    description: "For eligible travelers with prior dengue infection",
    schedule: "As per guidance",
    notes: "Dengue fever risk exists - use mosquito protection",
  },
  tickEncephalitis: {
    name: "Tick-borne Encephalitis",
    type: "optional" as const,
    description: "For travelers to forested areas in endemic regions",
    schedule: "3 doses",
    notes: "Consider for rural/forested travel in affected regions",
  },
};

// All countries from TravelHealthPro
export const travelDestinations: TravelDestination[] = [
  // Africa
  {
    id: "algeria",
    name: "Algeria",
    region: "North Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Ensure routine vaccinations current. Practice food and water safety. Limited malaria risk in some southern regions.",
  },
  {
    id: "angola",
    name: "Angola",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Ensure routine vaccinations current.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout. Antimalarials recommended.",
  },
  {
    id: "benin",
    name: "Benin",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria risk throughout. Meningitis risk during dry season.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "botswana",
    name: "Botswana",
    region: "Southern Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk in northern regions (Okavango Delta, Chobe). Ensure routine vaccinations current.",
    malariaRisk: "Seasonal risk in northern regions (Nov-May)",
  },
  {
    id: "burkina-faso",
    name: "Burkina Faso",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis belt region.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "burundi",
    name: "Burundi",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever recommended. Malaria prophylaxis essential. Ensure routine vaccinations current.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "cameroon",
    name: "Cameroon",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis risk in northern regions.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "cape-verde",
    name: "Cape Verde",
    region: "West Africa",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "Generally low risk. Ensure routine vaccinations current. Dengue and Zika present.",
    malariaRisk: "Very low risk on Santiago island only (Sep-Dec)",
  },
  {
    id: "central-african-republic",
    name: "Central African Republic",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Limited medical facilities.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "chad",
    name: "Chad",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis belt region.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "comoros",
    name: "Comoros",
    region: "East Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "Malaria risk present. Ensure routine vaccinations current. Practice food and water safety.",
    malariaRisk: "Risk throughout",
  },
  {
    id: "congo",
    name: "Congo (Republic of)",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Limited medical facilities.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "democratic-republic-congo",
    name: "Democratic Republic of the Congo",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Cholera outbreaks common.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "djibouti",
    name: "Djibouti",
    region: "East Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk present. Very hot climate. Ensure adequate hydration.",
    malariaRisk: "Low risk throughout",
  },
  {
    id: "egypt",
    name: "Egypt",
    region: "North Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Ensure routine vaccinations current. No malaria in main tourist areas. Practice food and water safety.",
  },
  {
    id: "equatorial-guinea",
    name: "Equatorial Guinea",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "eritrea",
    name: "Eritrea",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Malaria risk below 2,200m. Ensure routine vaccinations current.",
    malariaRisk: "Risk below 2,200m elevation",
  },
  {
    id: "eswatini",
    name: "Eswatini (Swaziland)",
    region: "Southern Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk in eastern lowlands. Ensure routine vaccinations current.",
    malariaRisk: "Risk in eastern regions (Sep-May)",
  },
  {
    id: "ethiopia",
    name: "Ethiopia",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever recommended for most areas. Malaria risk below 2,000m. Altitude considerations.",
    malariaRisk: "Risk below 2,000m elevation",
  },
  {
    id: "gabon",
    name: "Gabon",
    region: "Central Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "gambia",
    name: "The Gambia",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "ghana",
    name: "Ghana",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis risk in northern regions.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "guinea",
    name: "Guinea",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "guinea-bissau",
    name: "Guinea-Bissau",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "ivory-coast",
    name: "Côte d'Ivoire (Ivory Coast)",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "kenya",
    name: "Kenya",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential for most areas. Nairobi and highlands above 2,500m have low risk.",
    yellowFeverRequired: true,
    malariaRisk: "High risk except Nairobi and highlands above 2,500m",
  },
  {
    id: "lesotho",
    name: "Lesotho",
    region: "Southern Africa",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. High altitude country. Ensure routine vaccinations current.",
  },
  {
    id: "liberia",
    name: "Liberia",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "libya",
    name: "Libya",
    region: "North Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Ensure routine vaccinations current. Check travel advisories.",
  },
  {
    id: "madagascar",
    name: "Madagascar",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria prophylaxis essential. Plague outbreaks occur. Unique wildlife - rabies consideration.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "malawi",
    name: "Malawi",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Malaria prophylaxis essential. Cholera outbreaks occur. Bilharzia risk in Lake Malawi.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "mali",
    name: "Mali",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis belt region.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "mauritania",
    name: "Mauritania",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever recommended for southern regions. Malaria risk in southern areas. Meningitis belt.",
    malariaRisk: "Risk in southern regions",
  },
  {
    id: "mauritius",
    name: "Mauritius",
    region: "East Africa",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "morocco",
    name: "Morocco",
    region: "North Africa",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Generally low risk. No malaria. Ensure routine vaccinations current. Practice standard food and water precautions.",
  },
  {
    id: "mozambique",
    name: "Mozambique",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Malaria prophylaxis essential. Cholera outbreaks common. Bilharzia risk in freshwater.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "namibia",
    name: "Namibia",
    region: "Southern Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk in northern regions (Caprivi Strip, Kavango, Kunene). Ensure routine vaccinations current.",
    malariaRisk: "Seasonal risk in northern regions (Nov-Jun)",
  },
  {
    id: "niger",
    name: "Niger",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis belt region.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "nigeria",
    name: "Nigeria",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis risk in northern regions.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "rwanda",
    name: "Rwanda",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Malaria risk except Kigali. Gorilla trekking - rabies consideration. Ensure routine vaccinations current.",
    malariaRisk: "Risk throughout except Kigali",
  },
  {
    id: "sao-tome-principe",
    name: "São Tomé and Príncipe",
    region: "Central Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice: "Malaria risk present. Ensure routine vaccinations current.",
    malariaRisk: "Risk throughout",
  },
  {
    id: "senegal",
    name: "Senegal",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "seychelles",
    name: "Seychelles",
    region: "East Africa",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "sierra-leone",
    name: "Sierra Leone",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "somalia",
    name: "Somalia",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Malaria prophylaxis essential. Cholera outbreaks common. Very limited medical facilities.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "south-africa",
    name: "South Africa",
    region: "Southern Africa",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk in certain areas (Kruger, KwaZulu-Natal lowveld, Limpopo). Ensure routine vaccinations current.",
    malariaRisk: "Seasonal risk in Kruger/KZN/Limpopo (Sep-May)",
  },
  {
    id: "south-sudan",
    name: "South Sudan",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Very limited medical facilities.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "sudan",
    name: "Sudan",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Meningitis belt region.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "tanzania",
    name: "Tanzania",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Malaria prophylaxis essential. Yellow fever certificate required if arriving from endemic countries. Kilimanjaro altitude considerations.",
    malariaRisk: "High risk except highlands above 1,800m",
  },
  {
    id: "togo",
    name: "Togo",
    region: "West Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "tunisia",
    name: "Tunisia",
    region: "North Africa",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "uganda",
    name: "Uganda",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Gorilla trekking - rabies consideration.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout",
  },
  {
    id: "zambia",
    name: "Zambia",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Malaria prophylaxis essential. Yellow fever certificate required if arriving from endemic countries.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "zimbabwe",
    name: "Zimbabwe",
    region: "East Africa",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk in low-lying areas. Harare and highlands generally low risk. Cholera outbreaks occur.",
    malariaRisk: "Risk in low-lying areas, especially Zambezi valley",
  },

  // Asia
  {
    id: "afghanistan",
    name: "Afghanistan",
    region: "South Asia",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.meningococcal,
    ],
    generalAdvice:
      "Malaria risk below 2,000m. Ensure routine vaccinations current. Limited medical facilities.",
    malariaRisk: "Risk below 2,000m (May-Nov)",
  },
  {
    id: "armenia",
    name: "Armenia",
    region: "Caucasus",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice: "No malaria risk. Ensure routine vaccinations current.",
  },
  {
    id: "azerbaijan",
    name: "Azerbaijan",
    region: "Caucasus",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis risk in some areas. Ensure routine vaccinations current.",
  },
  {
    id: "bangladesh",
    name: "Bangladesh",
    region: "South Asia",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in Chittagong Hill Tracts. Japanese encephalitis risk. Dengue common in cities.",
    malariaRisk: "Risk in Chittagong Hill Tracts",
  },
  {
    id: "bhutan",
    name: "Bhutan",
    region: "South Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in southern belt districts. Altitude considerations. Japanese encephalitis in southern areas.",
    malariaRisk: "Low risk in southern districts",
  },
  {
    id: "brunei",
    name: "Brunei",
    region: "Southeast Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "cambodia",
    name: "Cambodia",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in forested/rural areas. Japanese encephalitis risk. Dengue common.",
    malariaRisk: "Risk in forested areas, low in Phnom Penh/Angkor",
  },
  {
    id: "china",
    name: "China",
    region: "East Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in limited areas (Yunnan, Hainan). Japanese encephalitis in rural areas. Tick-borne encephalitis in forested regions.",
    malariaRisk: "Low risk in limited rural areas (Yunnan, Hainan)",
  },
  {
    id: "georgia",
    name: "Georgia",
    region: "Caucasus",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice: "No malaria risk. Ensure routine vaccinations current.",
  },
  {
    id: "india",
    name: "India",
    region: "South Asia",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Malaria prophylaxis may be needed for some regions. Ensure routine vaccinations current. Japanese encephalitis in rural areas. Dengue common.",
    malariaRisk: "Risk varies by region; low in cities, higher in rural areas",
  },
  {
    id: "indonesia",
    name: "Indonesia",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in eastern Indonesia and rural areas. Bali and main cities generally safe. Japanese encephalitis in rural areas. Dengue common.",
    malariaRisk: "Risk in Papua and some eastern islands; low in Bali/Jakarta",
  },
  {
    id: "iran",
    name: "Iran",
    region: "Middle East",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "Limited malaria risk in southeastern provinces. Ensure routine vaccinations current.",
    malariaRisk: "Low risk in rural southeastern areas (Mar-Nov)",
  },
  {
    id: "iraq",
    name: "Iraq",
    region: "Middle East",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "No malaria risk. Ensure routine vaccinations current. Limited medical facilities in many areas.",
  },
  {
    id: "israel",
    name: "Israel",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "japan",
    name: "Japan",
    region: "East Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisB,
      commonVaccines.japaneseEncephalitis,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Japanese encephalitis in rural areas (summer). Excellent medical facilities.",
  },
  {
    id: "jordan",
    name: "Jordan",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "kazakhstan",
    name: "Kazakhstan",
    region: "Central Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in some areas. Ensure routine vaccinations current.",
  },
  {
    id: "kyrgyzstan",
    name: "Kyrgyzstan",
    region: "Central Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in some areas. Altitude considerations.",
  },
  {
    id: "laos",
    name: "Laos",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in forested/rural areas. Japanese encephalitis risk. Dengue present.",
    malariaRisk: "Risk in forested areas; low in Vientiane",
  },
  {
    id: "lebanon",
    name: "Lebanon",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice: "No malaria risk. Ensure routine vaccinations current.",
  },
  {
    id: "malaysia",
    name: "Malaysia",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in limited forested areas of Sabah/Sarawak. Kuala Lumpur safe. Dengue common. Japanese encephalitis in rural areas.",
    malariaRisk: "Limited risk in Sabah/Sarawak interior; none in KL",
  },
  {
    id: "maldives",
    name: "Maldives",
    region: "South Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "mongolia",
    name: "Mongolia",
    region: "East Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in some areas. Extreme climate considerations.",
  },
  {
    id: "myanmar",
    name: "Myanmar (Burma)",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in forested/border areas. Japanese encephalitis risk. Dengue present.",
    malariaRisk: "Risk in forested areas; low in Yangon/Mandalay",
  },
  {
    id: "nepal",
    name: "Nepal",
    region: "South Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in Terai region. Altitude considerations for trekking. Japanese encephalitis in lowlands.",
    malariaRisk: "Low risk in Terai region; none in Kathmandu/mountains",
  },
  {
    id: "north-korea",
    name: "North Korea",
    region: "East Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Limited medical facilities. Ensure routine vaccinations current.",
  },
  {
    id: "oman",
    name: "Oman",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "pakistan",
    name: "Pakistan",
    region: "South Asia",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk in rural areas below 2,000m. Polio vaccination essential. Dengue in cities.",
    malariaRisk: "Risk in rural areas below 2,000m",
  },
  {
    id: "palestinian-territories",
    name: "Palestinian Territories",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice: "No malaria risk. Ensure routine vaccinations current.",
  },
  {
    id: "philippines",
    name: "Philippines",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in rural areas of some islands. Dengue common. Japanese encephalitis in rural areas.",
    malariaRisk: "Low risk in rural areas of some islands; none in Manila",
  },
  {
    id: "qatar",
    name: "Qatar",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "russia",
    name: "Russia",
    region: "Europe/Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Ensure routine vaccinations current.",
  },
  {
    id: "saudi-arabia",
    name: "Saudi Arabia",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
      {
        ...commonVaccines.meningococcal,
        type: "required",
        description: "Required for Hajj/Umrah pilgrims",
        notes: "ACWY vaccine certificate required for pilgrims",
      },
    ],
    generalAdvice:
      "No malaria risk. Meningococcal ACWY vaccine required for Hajj/Umrah. Good medical facilities.",
  },
  {
    id: "singapore",
    name: "Singapore",
    region: "Southeast Asia",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.hepatitisB],
    generalAdvice:
      "No malaria risk. Dengue present. Excellent medical facilities. Generally low health risk.",
  },
  {
    id: "south-korea",
    name: "South Korea",
    region: "East Asia",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.hepatitisB],
    generalAdvice:
      "No malaria risk. Excellent medical facilities. Generally low health risk.",
  },
  {
    id: "sri-lanka",
    name: "Sri Lanka",
    region: "South Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Very low malaria risk. Dengue common in cities. Japanese encephalitis in rural areas.",
    malariaRisk: "Very low risk; prophylaxis usually not needed",
  },
  {
    id: "syria",
    name: "Syria",
    region: "Middle East",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "No malaria risk. Limited medical facilities. Ensure routine vaccinations current.",
  },
  {
    id: "taiwan",
    name: "Taiwan",
    region: "East Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisB,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Japanese encephalitis in rural areas (summer). Excellent medical facilities.",
  },
  {
    id: "tajikistan",
    name: "Tajikistan",
    region: "Central Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in some areas. Altitude considerations.",
  },
  {
    id: "thailand",
    name: "Thailand",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in limited forested border areas. Bangkok and main resorts safe. Dengue common. Japanese encephalitis in rural areas.",
    malariaRisk: "Limited risk in forested borders; none in Bangkok/resorts",
  },
  {
    id: "timor-leste",
    name: "Timor-Leste (East Timor)",
    region: "Southeast Asia",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Malaria risk throughout. Dengue present. Limited medical facilities.",
    malariaRisk: "Risk throughout",
  },
  {
    id: "turkey",
    name: "Turkey",
    region: "Europe/Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities in cities.",
  },
  {
    id: "turkmenistan",
    name: "Turkmenistan",
    region: "Central Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice: "No malaria risk. Ensure routine vaccinations current.",
  },
  {
    id: "united-arab-emirates",
    name: "United Arab Emirates",
    region: "Middle East",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "uzbekistan",
    name: "Uzbekistan",
    region: "Central Asia",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice: "No malaria risk. Ensure routine vaccinations current.",
  },
  {
    id: "vietnam",
    name: "Vietnam",
    region: "Southeast Asia",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria risk in some rural areas. Hanoi and Ho Chi Minh City safe. Dengue common. Japanese encephalitis in rural areas.",
    malariaRisk: "Low risk in rural areas; none in cities",
  },
  {
    id: "yemen",
    name: "Yemen",
    region: "Middle East",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Malaria risk throughout. Yellow fever certificate required if arriving from endemic countries. Very limited medical facilities.",
    malariaRisk: "Risk throughout below 2,000m",
  },

  // Americas
  {
    id: "argentina",
    name: "Argentina",
    region: "South America",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "Very limited malaria risk in northern border areas. Yellow fever recommended for Iguazu Falls/Misiones. Generally low risk.",
    malariaRisk: "Very low risk in Salta/northern borders",
  },
  {
    id: "belize",
    name: "Belize",
    region: "Central America",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Low malaria risk in rural areas. Belize City and resorts generally safe. Dengue and Zika present.",
    malariaRisk: "Low risk in rural areas; none in Belize City",
  },
  {
    id: "bolivia",
    name: "Bolivia",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for certain regions. Malaria risk in Amazon basin. Altitude considerations. Dengue present.",
    malariaRisk: "Risk in Amazon basin below 2,500m",
  },
  {
    id: "brazil",
    name: "Brazil",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for most regions. Malaria risk in Amazon. Dengue and Zika present - use mosquito protection.",
    malariaRisk: "Risk in Amazon; none in Rio/São Paulo",
  },
  {
    id: "canada",
    name: "Canada",
    region: "North America",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities. Ensure routine vaccinations current.",
  },
  {
    id: "chile",
    name: "Chile",
    region: "South America",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Easter Island has dengue. Generally low health risk. Good medical facilities in cities.",
  },
  {
    id: "colombia",
    name: "Colombia",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for many regions. Malaria risk in rural areas. Bogota and Cartagena safe. Dengue and Zika present.",
    malariaRisk: "Risk in rural areas below 1,600m; none in Bogota/Cartagena",
  },
  {
    id: "costa-rica",
    name: "Costa Rica",
    region: "Central America",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "Very low malaria risk in limited areas. Dengue and Zika present. Generally low risk. Good medical facilities.",
    malariaRisk: "Very low risk in limited areas",
  },
  {
    id: "cuba",
    name: "Cuba",
    region: "Caribbean",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Dengue present. Generally low health risk. Good medical care for tourists.",
  },
  {
    id: "dominican-republic",
    name: "Dominican Republic",
    region: "Caribbean",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "Low malaria risk in some western areas. Dengue and Zika present. Cholera outbreaks occur.",
    malariaRisk: "Low risk in western provinces; none in resorts",
  },
  {
    id: "ecuador",
    name: "Ecuador",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for Amazon and some coastal areas. Malaria risk in Amazon. Galapagos safe. Altitude considerations.",
    malariaRisk:
      "Risk in Amazon and some coastal areas; none in Quito/Galapagos",
  },
  {
    id: "el-salvador",
    name: "El Salvador",
    region: "Central America",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Dengue and Zika present. Ensure routine vaccinations current.",
  },
  {
    id: "french-guiana",
    name: "French Guiana",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Dengue and Zika present.",
    yellowFeverRequired: true,
    malariaRisk: "High risk throughout except coastal cities",
  },
  {
    id: "guatemala",
    name: "Guatemala",
    region: "Central America",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Low malaria risk in rural areas below 1,500m. Antigua and main cities safe. Dengue and Zika present.",
    malariaRisk: "Low risk in rural areas below 1,500m",
  },
  {
    id: "guyana",
    name: "Guyana",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Dengue present.",
    yellowFeverRequired: true,
    malariaRisk: "Risk throughout except Georgetown",
  },
  {
    id: "haiti",
    name: "Haiti",
    region: "Caribbean",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.cholera,
    ],
    generalAdvice:
      "Low malaria risk. Dengue and Zika present. Cholera outbreaks common. Limited medical facilities.",
    malariaRisk: "Low risk throughout",
  },
  {
    id: "honduras",
    name: "Honduras",
    region: "Central America",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Low malaria risk in rural areas. Roatan and Bay Islands safe. Dengue and Zika present.",
    malariaRisk: "Low risk in rural areas; none in cities/Bay Islands",
  },
  {
    id: "jamaica",
    name: "Jamaica",
    region: "Caribbean",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Dengue present. Generally low health risk.",
  },
  {
    id: "mexico",
    name: "Mexico",
    region: "North America",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Low malaria risk in limited rural areas. Cancun and main resorts safe. Dengue and Zika in some areas.",
    malariaRisk: "Low risk in limited rural southern areas",
  },
  {
    id: "nicaragua",
    name: "Nicaragua",
    region: "Central America",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Low malaria risk in rural areas. Dengue and Zika present. Ensure routine vaccinations current.",
    malariaRisk: "Low risk in rural areas",
  },
  {
    id: "panama",
    name: "Panama",
    region: "Central America",
    riskLevel: "medium",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for certain regions. Low malaria risk in some areas. Panama City safe. Dengue present.",
    malariaRisk: "Low risk in eastern provinces; none in Panama City/Canal",
  },
  {
    id: "paraguay",
    name: "Paraguay",
    region: "South America",
    riskLevel: "medium",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for certain regions. Low malaria risk in limited areas. Dengue present.",
    malariaRisk: "Very low risk in limited areas",
  },
  {
    id: "peru",
    name: "Peru",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for Amazon and jungle regions. Malaria risk in Amazon. Lima and Machu Picchu safe. Altitude considerations.",
    malariaRisk: "Risk in Amazon below 2,000m; none in Lima/Cusco/Machu Picchu",
  },
  {
    id: "suriname",
    name: "Suriname",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "required" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever certificate mandatory. Malaria prophylaxis essential. Dengue present.",
    yellowFeverRequired: true,
    malariaRisk: "Risk in interior; low in Paramaribo",
  },
  {
    id: "trinidad-tobago",
    name: "Trinidad and Tobago",
    region: "Caribbean",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "No malaria risk. Dengue present. Rabies risk from bats. Generally low health risk.",
  },
  {
    id: "usa",
    name: "United States of America",
    region: "North America",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities (but expensive). Ensure routine vaccinations current.",
  },
  {
    id: "uruguay",
    name: "Uruguay",
    region: "South America",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities.",
  },
  {
    id: "venezuela",
    name: "Venezuela",
    region: "South America",
    riskLevel: "high",
    vaccines: [
      { ...commonVaccines.yellowFever, type: "recommended" },
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
    ],
    generalAdvice:
      "Yellow fever recommended for many regions. Malaria risk in rural areas. Caracas safe from malaria. Dengue present. Limited medical facilities.",
    malariaRisk: "Risk in rural areas below 1,700m; none in Caracas",
  },

  // Europe
  {
    id: "albania",
    name: "Albania",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "andorra",
    name: "Andorra",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities.",
  },
  {
    id: "austria",
    name: "Austria",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Excellent medical facilities.",
  },
  {
    id: "belarus",
    name: "Belarus",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Ensure routine vaccinations current.",
  },
  {
    id: "belgium",
    name: "Belgium",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "bosnia-herzegovina",
    name: "Bosnia and Herzegovina",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Land mine risk in some rural areas.",
  },
  {
    id: "bulgaria",
    name: "Bulgaria",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Generally low health risk.",
  },
  {
    id: "croatia",
    name: "Croatia",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Generally low health risk. Good medical facilities.",
  },
  {
    id: "cyprus",
    name: "Cyprus",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities.",
  },
  {
    id: "czech-republic",
    name: "Czech Republic",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Excellent medical facilities.",
  },
  {
    id: "denmark",
    name: "Denmark",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "estonia",
    name: "Estonia",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Generally low health risk.",
  },
  {
    id: "finland",
    name: "Finland",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in Åland Islands and southern forests. Excellent medical facilities.",
  },
  {
    id: "france",
    name: "France",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in Alsace region. Excellent medical facilities.",
  },
  {
    id: "germany",
    name: "Germany",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in southern regions. Excellent medical facilities.",
  },
  {
    id: "greece",
    name: "Greece",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities in cities.",
  },
  {
    id: "hungary",
    name: "Hungary",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Good medical facilities.",
  },
  {
    id: "iceland",
    name: "Iceland",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "ireland",
    name: "Ireland",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "italy",
    name: "Italy",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in northeastern forested areas. Excellent medical facilities.",
  },
  {
    id: "kosovo",
    name: "Kosovo",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "latvia",
    name: "Latvia",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Generally low health risk.",
  },
  {
    id: "liechtenstein",
    name: "Liechtenstein",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis possible in forested areas. Excellent medical facilities.",
  },
  {
    id: "lithuania",
    name: "Lithuania",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Generally low health risk.",
  },
  {
    id: "luxembourg",
    name: "Luxembourg",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "malta",
    name: "Malta",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities.",
  },
  {
    id: "moldova",
    name: "Moldova",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "monaco",
    name: "Monaco",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "montenegro",
    name: "Montenegro",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities in cities.",
  },
  {
    id: "netherlands",
    name: "Netherlands",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "north-macedonia",
    name: "North Macedonia",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Ensure routine vaccinations current.",
  },
  {
    id: "norway",
    name: "Norway",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "poland",
    name: "Poland",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Good medical facilities.",
  },
  {
    id: "portugal",
    name: "Portugal",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities.",
  },
  {
    id: "romania",
    name: "Romania",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Generally low health risk.",
  },
  {
    id: "san-marino",
    name: "San Marino",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "serbia",
    name: "Serbia",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "No malaria risk. Generally low health risk. Good medical facilities in cities.",
  },
  {
    id: "slovakia",
    name: "Slovakia",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Good medical facilities.",
  },
  {
    id: "slovenia",
    name: "Slovenia",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Excellent medical facilities.",
  },
  {
    id: "spain",
    name: "Spain",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "sweden",
    name: "Sweden",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Excellent medical facilities.",
  },
  {
    id: "switzerland",
    name: "Switzerland",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.tickEncephalitis],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in some areas. Excellent medical facilities.",
  },
  {
    id: "ukraine",
    name: "Ukraine",
    region: "Europe",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.hepatitisB,
      commonVaccines.tickEncephalitis,
    ],
    generalAdvice:
      "No malaria risk. Tick-borne encephalitis in forested areas. Check travel advisories.",
  },
  {
    id: "united-kingdom",
    name: "United Kingdom",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "vatican-city",
    name: "Vatican City",
    region: "Europe",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities in Rome.",
  },

  // Oceania
  {
    id: "australia",
    name: "Australia",
    region: "Oceania",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK, commonVaccines.japaneseEncephalitis],
    generalAdvice:
      "No malaria risk. Japanese encephalitis in limited northern areas. Excellent medical facilities.",
  },
  {
    id: "fiji",
    name: "Fiji",
    region: "Oceania",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Dengue and Zika present. Generally low health risk.",
  },
  {
    id: "french-polynesia",
    name: "French Polynesia (Tahiti)",
    region: "Oceania",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Dengue and Zika present. Generally low health risk.",
  },
  {
    id: "new-caledonia",
    name: "New Caledonia",
    region: "Oceania",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Dengue present. Generally low health risk. Good medical facilities.",
  },
  {
    id: "new-zealand",
    name: "New Zealand",
    region: "Oceania",
    riskLevel: "low",
    vaccines: [commonVaccines.routineUK],
    generalAdvice:
      "No malaria risk. Generally low health risk. Excellent medical facilities.",
  },
  {
    id: "papua-new-guinea",
    name: "Papua New Guinea",
    region: "Oceania",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
      commonVaccines.rabies,
      commonVaccines.japaneseEncephalitis,
    ],
    generalAdvice:
      "Malaria prophylaxis essential throughout. Japanese encephalitis risk. Limited medical facilities.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "samoa",
    name: "Samoa",
    region: "Oceania",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Dengue and Zika present. Generally low health risk.",
  },
  {
    id: "solomon-islands",
    name: "Solomon Islands",
    region: "Oceania",
    riskLevel: "high",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "Malaria prophylaxis essential. Dengue present. Limited medical facilities.",
    malariaRisk: "High risk throughout",
  },
  {
    id: "tonga",
    name: "Tonga",
    region: "Oceania",
    riskLevel: "low",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
    ],
    generalAdvice:
      "No malaria risk. Dengue and Zika present. Limited medical facilities.",
  },
  {
    id: "vanuatu",
    name: "Vanuatu",
    region: "Oceania",
    riskLevel: "medium",
    vaccines: [
      commonVaccines.routineUK,
      commonVaccines.hepatitisA,
      commonVaccines.typhoid,
      commonVaccines.hepatitisB,
    ],
    generalAdvice:
      "Malaria risk present. Dengue present. Limited medical facilities.",
    malariaRisk: "Risk throughout",
  },
];

// Export function to get destination by ID
export function getDestinationById(id: string): TravelDestination | undefined {
  return travelDestinations.find((dest) => dest.id === id);
}

// Export function to search destinations
export function searchDestinations(query: string): TravelDestination[] {
  const lowerQuery = query.toLowerCase();
  return travelDestinations.filter(
    (dest) =>
      dest.name.toLowerCase().includes(lowerQuery) ||
      dest.region.toLowerCase().includes(lowerQuery),
  );
}

// Export function to get destinations by region
export function getDestinationsByRegion(region: string): TravelDestination[] {
  return travelDestinations.filter((dest) => dest.region === region);
}

// Export all unique regions
export const regions = Array.from(
  new Set(travelDestinations.map((dest) => dest.region)),
).sort();

// NaTHNaC Travel Health Pro country URL mapping
// Source: https://travelhealthpro.org.uk/countries
const travelHealthProUrls: Record<string, string> = {
  afghanistan: "https://travelhealthpro.org.uk/country/1/afghanistan",
  albania: "https://travelhealthpro.org.uk/country/2/albania",
  algeria: "https://travelhealthpro.org.uk/country/3/algeria",
  andorra: "https://travelhealthpro.org.uk/country/6/andorra",
  angola: "https://travelhealthpro.org.uk/country/7/angola",
  argentina: "https://travelhealthpro.org.uk/country/11/argentina",
  armenia: "https://travelhealthpro.org.uk/country/12/armenia",
  australia: "https://travelhealthpro.org.uk/country/15/australia",
  austria: "https://travelhealthpro.org.uk/country/16/austria",
  azerbaijan: "https://travelhealthpro.org.uk/country/17/azerbaijan",
  bangladesh: "https://travelhealthpro.org.uk/country/22/bangladesh",
  belarus: "https://travelhealthpro.org.uk/country/24/belarus",
  belgium: "https://travelhealthpro.org.uk/country/25/belgium",
  belize: "https://travelhealthpro.org.uk/country/26/belize",
  benin: "https://travelhealthpro.org.uk/country/27/benin",
  bhutan: "https://travelhealthpro.org.uk/country/29/bhutan",
  bolivia: "https://travelhealthpro.org.uk/country/30/bolivia",
  "bosnia-herzegovina":
    "https://travelhealthpro.org.uk/country/32/bosnia-and-herzegovina",
  botswana: "https://travelhealthpro.org.uk/country/33/botswana",
  brazil: "https://travelhealthpro.org.uk/country/34/brazil",
  brunei: "https://travelhealthpro.org.uk/country/35/brunei",
  bulgaria: "https://travelhealthpro.org.uk/country/36/bulgaria",
  "burkina-faso": "https://travelhealthpro.org.uk/country/37/burkina-faso",
  burundi: "https://travelhealthpro.org.uk/country/38/burundi",
  cambodia: "https://travelhealthpro.org.uk/country/39/cambodia",
  cameroon: "https://travelhealthpro.org.uk/country/40/cameroon",
  canada: "https://travelhealthpro.org.uk/country/41/canada",
  "cape-verde": "https://travelhealthpro.org.uk/country/43/cape-verde",
  "central-african-republic":
    "https://travelhealthpro.org.uk/country/46/central-african-republic",
  chad: "https://travelhealthpro.org.uk/country/47/chad",
  chile: "https://travelhealthpro.org.uk/country/48/chile",
  china: "https://travelhealthpro.org.uk/country/49/china",
  colombia: "https://travelhealthpro.org.uk/country/53/colombia",
  comoros: "https://travelhealthpro.org.uk/country/54/comoros",
  congo: "https://travelhealthpro.org.uk/country/55/congo",
  "costa-rica": "https://travelhealthpro.org.uk/country/58/costa-rica",
  croatia: "https://travelhealthpro.org.uk/country/59/croatia",
  cuba: "https://travelhealthpro.org.uk/country/60/cuba",
  cyprus: "https://travelhealthpro.org.uk/country/61/cyprus",
  "czech-republic": "https://travelhealthpro.org.uk/country/62/czechia",
  "democratic-republic-congo":
    "https://travelhealthpro.org.uk/country/63/democratic-republic-of-the-congo",
  denmark: "https://travelhealthpro.org.uk/country/64/denmark",
  djibouti: "https://travelhealthpro.org.uk/country/65/djibouti",
  "dominican-republic":
    "https://travelhealthpro.org.uk/country/67/dominican-republic",
  ecuador: "https://travelhealthpro.org.uk/country/70/ecuador",
  egypt: "https://travelhealthpro.org.uk/country/71/egypt",
  "el-salvador": "https://travelhealthpro.org.uk/country/72/el-salvador",
  "equatorial-guinea":
    "https://travelhealthpro.org.uk/country/73/equatorial-guinea",
  eritrea: "https://travelhealthpro.org.uk/country/74/eritrea",
  eswatini: "https://travelhealthpro.org.uk/country/214/eswatini",
  estonia: "https://travelhealthpro.org.uk/country/75/estonia",
  ethiopia: "https://travelhealthpro.org.uk/country/76/ethiopia",
  fiji: "https://travelhealthpro.org.uk/country/79/fiji",
  finland: "https://travelhealthpro.org.uk/country/80/finland",
  france: "https://travelhealthpro.org.uk/country/81/france",
  "french-guiana": "https://travelhealthpro.org.uk/country/82/french-guiana",
  "french-polynesia":
    "https://travelhealthpro.org.uk/country/83/french-polynesia",
  gabon: "https://travelhealthpro.org.uk/country/84/gabon",
  gambia: "https://travelhealthpro.org.uk/country/86/the-gambia",
  georgia: "https://travelhealthpro.org.uk/country/87/georgia",
  germany: "https://travelhealthpro.org.uk/country/88/germany",
  ghana: "https://travelhealthpro.org.uk/country/89/ghana",
  greece: "https://travelhealthpro.org.uk/country/91/greece",
  guatemala: "https://travelhealthpro.org.uk/country/96/guatemala",
  guinea: "https://travelhealthpro.org.uk/country/97/guinea",
  "guinea-bissau": "https://travelhealthpro.org.uk/country/98/guinea-bissau",
  guyana: "https://travelhealthpro.org.uk/country/99/guyana",
  haiti: "https://travelhealthpro.org.uk/country/100/haiti",
  honduras: "https://travelhealthpro.org.uk/country/101/honduras",
  hungary: "https://travelhealthpro.org.uk/country/103/hungary",
  iceland: "https://travelhealthpro.org.uk/country/104/iceland",
  india: "https://travelhealthpro.org.uk/country/105/india",
  indonesia: "https://travelhealthpro.org.uk/country/106/indonesia",
  iran: "https://travelhealthpro.org.uk/country/107/iran",
  iraq: "https://travelhealthpro.org.uk/country/108/iraq",
  ireland: "https://travelhealthpro.org.uk/country/109/ireland",
  israel: "https://travelhealthpro.org.uk/country/110/israel",
  italy: "https://travelhealthpro.org.uk/country/111/italy",
  "ivory-coast": "https://travelhealthpro.org.uk/country/112/cte-divoire",
  jamaica: "https://travelhealthpro.org.uk/country/113/jamaica",
  japan: "https://travelhealthpro.org.uk/country/114/japan",
  jordan: "https://travelhealthpro.org.uk/country/115/jordan",
  kazakhstan: "https://travelhealthpro.org.uk/country/116/kazakhstan",
  kenya: "https://travelhealthpro.org.uk/country/117/kenya",
  kosovo: "https://travelhealthpro.org.uk/country/121/kosovo",
  kyrgyzstan: "https://travelhealthpro.org.uk/country/123/kyrgyzstan",
  laos: "https://travelhealthpro.org.uk/country/124/laos",
  latvia: "https://travelhealthpro.org.uk/country/125/latvia",
  lebanon: "https://travelhealthpro.org.uk/country/126/lebanon",
  lesotho: "https://travelhealthpro.org.uk/country/127/lesotho",
  liberia: "https://travelhealthpro.org.uk/country/128/liberia",
  libya: "https://travelhealthpro.org.uk/country/129/libya",
  liechtenstein: "https://travelhealthpro.org.uk/country/130/liechtenstein",
  lithuania: "https://travelhealthpro.org.uk/country/131/lithuania",
  luxembourg: "https://travelhealthpro.org.uk/country/132/luxembourg",
  madagascar: "https://travelhealthpro.org.uk/country/134/madagascar",
  malawi: "https://travelhealthpro.org.uk/country/136/malawi",
  malaysia: "https://travelhealthpro.org.uk/country/137/malaysia",
  maldives: "https://travelhealthpro.org.uk/country/139/maldives",
  mali: "https://travelhealthpro.org.uk/country/140/mali",
  malta: "https://travelhealthpro.org.uk/country/141/malta",
  mauritania: "https://travelhealthpro.org.uk/country/144/mauritania",
  mauritius: "https://travelhealthpro.org.uk/country/145/mauritius",
  mexico: "https://travelhealthpro.org.uk/country/147/mexico",
  moldova: "https://travelhealthpro.org.uk/country/149/moldova",
  monaco: "https://travelhealthpro.org.uk/country/150/monaco",
  mongolia: "https://travelhealthpro.org.uk/country/151/mongolia",
  montenegro: "https://travelhealthpro.org.uk/country/152/montenegro",
  morocco: "https://travelhealthpro.org.uk/country/154/morocco",
  mozambique: "https://travelhealthpro.org.uk/country/155/mozambique",
  myanmar: "https://travelhealthpro.org.uk/country/156/myanmar-burma",
  namibia: "https://travelhealthpro.org.uk/country/157/namibia",
  nepal: "https://travelhealthpro.org.uk/country/159/nepal",
  netherlands: "https://travelhealthpro.org.uk/country/160/netherlands",
  "new-caledonia": "https://travelhealthpro.org.uk/country/161/new-caledonia",
  "new-zealand": "https://travelhealthpro.org.uk/country/162/new-zealand",
  nicaragua: "https://travelhealthpro.org.uk/country/163/nicaragua",
  niger: "https://travelhealthpro.org.uk/country/164/niger",
  nigeria: "https://travelhealthpro.org.uk/country/165/nigeria",
  "north-korea": "https://travelhealthpro.org.uk/country/119/north-korea",
  "north-macedonia":
    "https://travelhealthpro.org.uk/country/133/north-macedonia",
  norway: "https://travelhealthpro.org.uk/country/168/norway",
  oman: "https://travelhealthpro.org.uk/country/170/oman",
  pakistan: "https://travelhealthpro.org.uk/country/171/pakistan",
  "palestinian-territories":
    "https://travelhealthpro.org.uk/country/173/palestine",
  panama: "https://travelhealthpro.org.uk/country/174/panama",
  "papua-new-guinea":
    "https://travelhealthpro.org.uk/country/175/papua-new-guinea",
  paraguay: "https://travelhealthpro.org.uk/country/176/paraguay",
  peru: "https://travelhealthpro.org.uk/country/177/peru",
  philippines: "https://travelhealthpro.org.uk/country/178/philippines",
  poland: "https://travelhealthpro.org.uk/country/180/poland",
  portugal: "https://travelhealthpro.org.uk/country/181/portugal",
  qatar: "https://travelhealthpro.org.uk/country/183/qatar",
  romania: "https://travelhealthpro.org.uk/country/185/romania",
  russia: "https://travelhealthpro.org.uk/country/186/russia",
  rwanda: "https://travelhealthpro.org.uk/country/187/rwanda",
  samoa: "https://travelhealthpro.org.uk/country/188/samoa",
  "san-marino": "https://travelhealthpro.org.uk/country/189/san-marino",
  "sao-tome-principe":
    "https://travelhealthpro.org.uk/country/190/sao-tome-and-principe",
  "saudi-arabia": "https://travelhealthpro.org.uk/country/191/saudi-arabia",
  senegal: "https://travelhealthpro.org.uk/country/192/senegal",
  serbia: "https://travelhealthpro.org.uk/country/193/serbia",
  seychelles: "https://travelhealthpro.org.uk/country/194/seychelles",
  "sierra-leone": "https://travelhealthpro.org.uk/country/195/sierra-leone",
  singapore: "https://travelhealthpro.org.uk/country/196/singapore",
  slovakia: "https://travelhealthpro.org.uk/country/197/slovakia",
  slovenia: "https://travelhealthpro.org.uk/country/198/slovenia",
  "solomon-islands":
    "https://travelhealthpro.org.uk/country/199/solomon-islands",
  somalia: "https://travelhealthpro.org.uk/country/200/somalia",
  "south-africa": "https://travelhealthpro.org.uk/country/201/south-africa",
  "south-korea": "https://travelhealthpro.org.uk/country/120/south-korea",
  "south-sudan": "https://travelhealthpro.org.uk/country/202/south-sudan",
  spain: "https://travelhealthpro.org.uk/country/203/spain",
  "sri-lanka": "https://travelhealthpro.org.uk/country/204/sri-lanka",
  sudan: "https://travelhealthpro.org.uk/country/212/sudan",
  suriname: "https://travelhealthpro.org.uk/country/213/suriname",
  sweden: "https://travelhealthpro.org.uk/country/215/sweden",
  switzerland: "https://travelhealthpro.org.uk/country/216/switzerland",
  syria: "https://travelhealthpro.org.uk/country/217/syria",
  taiwan: "https://travelhealthpro.org.uk/country/218/taiwan",
  tajikistan: "https://travelhealthpro.org.uk/country/219/tajikistan",
  tanzania: "https://travelhealthpro.org.uk/country/220/tanzania",
  thailand: "https://travelhealthpro.org.uk/country/221/thailand",
  "timor-leste": "https://travelhealthpro.org.uk/country/68/timor-leste",
  togo: "https://travelhealthpro.org.uk/country/222/togo",
  tonga: "https://travelhealthpro.org.uk/country/224/tonga",
  "trinidad-tobago":
    "https://travelhealthpro.org.uk/country/225/trinidad-and-tobago",
  tunisia: "https://travelhealthpro.org.uk/country/226/tunisia",
  turkey: "https://travelhealthpro.org.uk/country/227/turkey",
  turkmenistan: "https://travelhealthpro.org.uk/country/228/turkmenistan",
  uganda: "https://travelhealthpro.org.uk/country/231/uganda",
  ukraine: "https://travelhealthpro.org.uk/country/232/ukraine",
  "united-arab-emirates":
    "https://travelhealthpro.org.uk/country/233/united-arab-emirates",
  "united-kingdom": "https://travelhealthpro.org.uk/country/234/united-kingdom",
  uruguay: "https://travelhealthpro.org.uk/country/236/uruguay",
  usa: "https://travelhealthpro.org.uk/country/235/usa",
  uzbekistan: "https://travelhealthpro.org.uk/country/237/uzbekistan",
  vanuatu: "https://travelhealthpro.org.uk/country/238/vanuatu",
  venezuela: "https://travelhealthpro.org.uk/country/239/venezuela",
  vietnam: "https://travelhealthpro.org.uk/country/240/vietnam",
  yemen: "https://travelhealthpro.org.uk/country/246/yemen",
  zambia: "https://travelhealthpro.org.uk/country/247/zambia",
  zimbabwe: "https://travelhealthpro.org.uk/country/248/zimbabwe",
};

// Get NaTHNaC Travel Health Pro URL for a destination
export function getTravelHealthProUrl(destinationId: string): string {
  return (
    travelHealthProUrls[destinationId] ||
    "https://travelhealthpro.org.uk/countries"
  );
}
